import { Component, OnDestroy, OnInit, effect, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of, Subscription } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ResponseDocumentSubtype, SCOPE_DCE, SCOPE_INTERNAL_DOC } from '../../models/response-document-subtype';
import { DEFAULT_PROJECT_NAME, ResponseProjectDetail } from '../../models/response-project';
import {
  DocumentProcessingStepEnum,
  isArchiveFile,
  isDocumentIndexationPending,
  ResponseProjectUploadFile,
} from '../../models/response-project-document';
import { ApiResponseDocumentSubtypesService } from '../../services/api/response-document-subtypes/api-response-document-subtypes.service';
import { ApiResponseDocumentsService } from '../../services/api/response-documents/api-response-documents.service';
import { ApiResponseProjectsService } from '../../services/api/response-projects/api-response-projects.service';
import { TendersResponsesService } from '../../services/tenders-responses.service';
import { ResponseBlockedModalService } from '../../services/response-blocked-modal.service';
import { ResponseModuleState } from '../../utils/response-module.utils';
import { ContactReason, getCsmMailtoHref } from '../../utils/response-mailto.utils';
import { ResponseModuleStateService } from '../../services/response-module-state.service';
import { ResponseToastService } from '../../services/response-toast.service';
import { AutoUnsubscribe } from '../../../common-explain/decorators/auto-unsubscribe';
import { ResponseDialogComponent, ResponseDialogMode } from '../response-dialog/response-dialog.component';
import { sortInternalDocumentSubtypes } from '../documents/document-types';

const SUBTYPE_FIELDS = ['id', 'name', 'displayed_name', 'scope'];
const DEFAULT_FIELDS_DOCUMENTS = [
  'id',
  'uid',
  'name',
  'custom_name',
  'file_size',
  's3_key',
  'response_document_subtype_id',
  'status',
  'indexation_status',
  'is_in_library',
];
const DOCUMENTS_LIMIT = 500;

@AutoUnsubscribe
@Component({
  selector: 'app-response-project-view',
  templateUrl: './response-project-view.component.html',
  styleUrls: ['./response-project-view.component.scss'],
})
export class ResponseProjectViewComponent implements OnInit, OnDestroy {
  readonly project = signal<ResponseProjectDetail | null>(null);
  private projectSub: Subscription | null = null;
  private accountInfoSub: Subscription | null = null;

  accountState: ResponseModuleState | null = null;

  get isExpired(): boolean {
    return this.accountState?.isExpired ?? false;
  }
  get isTrial(): boolean {
    return this.accountState?.isTrial ?? false;
  }
  get expiredPlanLabel(): string {
    return this.accountState?.planLabel ?? '';
  }
  get planLabel(): string {
    return this.accountState?.planLabel ?? '';
  }
  get isClosed(): boolean {
    return this.project()?.is_active === false;
  }

  documentSubtypes: ResponseDocumentSubtype[] = [];

  isEditingTitle = false;
  editedTitle = DEFAULT_PROJECT_NAME;
  addinBannerExpanded = false;
  readonly addinInstalled = this.tendersResponsesService.addinInstalled;
  blocksRevealed = false;

  dceSubtypeOptions: ResponseDocumentSubtype[] = [];
  internalSubtypeOptions: ResponseDocumentSubtype[] = [];
  loadingDocuments = false;
  subtypeById = new Map<number, ResponseDocumentSubtype>();
  isNewProjectRoute = false;
  private currentProjectId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private apiSubtypes: ApiResponseDocumentSubtypesService,
    private apiResponseProjects: ApiResponseProjectsService,
    private apiDocuments: ApiResponseDocumentsService,
    private tendersResponsesService: TendersResponsesService,
    private blockedModalService: ResponseBlockedModalService,
    private responseModuleStateService: ResponseModuleStateService,
    private toastService: ResponseToastService,
    private translateService: TranslateService
  ) {
    effect(() => {
      const projects = this.tendersResponsesService.projects();
      untracked(() => {
        this.project.update(p => {
          if (!p) return p;
          const updated = projects.find(proj => proj.id === p.id);
          return updated && updated.name !== p.name ? { ...p, name: updated.name } : p;
        });
      });
    });
  }

  get defaultDceSubtypeId(): number | undefined {
    return this.documentSubtypes.find(s => s.name === 'undefined' && s.scope === SCOPE_DCE)?.id;
  }

  get defaultInternalSubtypeId(): number | undefined {
    return this.documentSubtypes.find(s => s.name === 'undefined' && s.scope === SCOPE_INTERNAL_DOC)?.id;
  }

  ngOnInit(): void {
    this.accountInfoSub = this.responseModuleStateService.state$.subscribe(state => {
      this.accountState = state;
    });

    this.apiSubtypes
      .search({
        fields: SUBTYPE_FIELDS,
        filters: {},
        limit: 100,
        offset: 0,
      })
      .subscribe({
        next: res => {
          this.documentSubtypes = res.data;
          this.buildSubtypeMaps();
        },
      });

    this.projectSub = this.route.paramMap
      .pipe(
        switchMap(params => {
          const id = params.get('uid');
          const wasNew = this.isNewProjectRoute;
          this.isNewProjectRoute = id === 'new';
          if (this.isNewProjectRoute) {
            this.editedTitle = DEFAULT_PROJECT_NAME;
            this.isEditingTitle = false;
            this.tendersResponsesService.newProjectId.set(null);
          }
          if (wasNew && !this.isNewProjectRoute) {
            this.tendersResponsesService.removeProject('new');
          }
          this.currentProjectId = id && id !== 'new' ? id : null;
          this.blocksRevealed = false;
          this.loadDocumentsIfNeeded();
          return this.currentProjectId
            ? this.apiResponseProjects.getById(this.currentProjectId).pipe(
                catchError((err: { status?: number }) => {
                  if (err.status === 404) {
                    window.location.href = '/tenders/responses';
                  }
                  return of(null);
                })
              )
            : of(null);
        })
      )
      .subscribe(p => this.project.set(p));
  }

  ngOnDestroy(): void {
    if (this.isNewProjectRoute) {
      this.tendersResponsesService.removeProject('new');
    }
  }

  private buildSubtypeMaps(): void {
    this.subtypeById = new Map(this.documentSubtypes.map(s => [s.id, s]));

    const grouped = this.documentSubtypes
      .filter(s => s.name !== 'undefined')
      .reduce(
        (acc, s) => {
          if (!acc[s.scope]) acc[s.scope] = [];
          acc[s.scope].push(s);
          return acc;
        },
        {} as Record<string, ResponseDocumentSubtype[]>
      );

    this.dceSubtypeOptions = (grouped[SCOPE_DCE] ?? []).sort((a, b) =>
      a.displayedName.localeCompare(b.displayedName, undefined, { sensitivity: 'base' })
    );
    this.internalSubtypeOptions = sortInternalDocumentSubtypes(grouped[SCOPE_INTERNAL_DOC] ?? []);

    this.loadDocumentsIfNeeded();
  }

  private loadDocumentsIfNeeded(): void {
    if (!this.currentProjectId || !this.subtypeById.size) return;
    // Just-created projects have no server docs yet; a search would return [] and wipe the in-flight import.
    if (this.tendersResponsesService.newProjectId() === this.currentProjectId) return;
    this.loadDocuments();
  }

  loadDocuments(): void {
    if (!this.currentProjectId) return;
    this.loadingDocuments = true;

    this.apiDocuments
      .search({
        fields: DEFAULT_FIELDS_DOCUMENTS,
        filters: { response_project_id: this.currentProjectId },
        limit: DOCUMENTS_LIMIT,
        offset: 0,
      })
      .subscribe({
        next: res => {
          const inProgressSteps = this.getInProgressSteps();
          const hasUploadedDocs = res.data.some(doc => doc.status === 'uploaded');

          const filesByScope = res.data
            .filter(doc => doc.status !== 'uploading')
            .flatMap(doc => {
              const subtype = this.subtypeById.get(doc.responseDocumentSubtypeId ?? '');
              const scope = subtype?.scope;
              if (scope !== SCOPE_DCE && scope !== SCOPE_INTERNAL_DOC) return [];

              const processingStep =
                inProgressSteps.get(doc.uid) ??
                (subtype?.name !== 'undefined'
                  ? isDocumentIndexationPending(doc)
                    ? DocumentProcessingStepEnum.INDEXING
                    : DocumentProcessingStepEnum.CATEGORIZED
                  : doc.status === 'archive'
                    ? DocumentProcessingStepEnum.EXTRACTING
                    : DocumentProcessingStepEnum.UNCATEGORIZED);

              const file: ResponseProjectUploadFile = {
                uid: doc.uid,
                name: doc.name,
                size: doc.fileSize,
                subtype: subtype?.name === 'undefined' ? undefined : subtype?.displayedName,
                subtypeId: subtype?.name === 'undefined' ? undefined : doc.responseDocumentSubtypeId,
                customName: doc.customName ?? null,
                documentId: Number(doc.id),
                processingStep,
                hidden: isArchiveFile(doc.name) && hasUploadedDocs,
                isInLibrary: !!doc.isInLibrary,
              };

              return [{ file, scope }];
            });

          filesByScope.sort((a, b) => a.scope.localeCompare(b.scope) || a.file.name.localeCompare(b.file.name));

          const dceFiles = filesByScope.filter(x => x.scope === SCOPE_DCE).map(x => x.file);
          const internalFiles = filesByScope.filter(x => x.scope === SCOPE_INTERNAL_DOC).map(x => x.file);

          if (!this.tendersResponsesService.hasActiveProcessing(this.currentProjectId!, SCOPE_DCE)) {
            this.tendersResponsesService.setFiles(this.currentProjectId!, SCOPE_DCE, dceFiles);
          }
          if (!this.tendersResponsesService.hasActiveProcessing(this.currentProjectId!, SCOPE_INTERNAL_DOC)) {
            this.tendersResponsesService.setFiles(this.currentProjectId!, SCOPE_INTERNAL_DOC, internalFiles);
          }

          this.loadingDocuments = false;
          if (dceFiles.length > 0) {
            this.blocksRevealed = true;
            this.tendersResponsesService.newProjectId.set(null);
          }
        },
        error: () => {
          this.loadingDocuments = false;
        },
      });
  }

  private getInProgressSteps(): Map<string, DocumentProcessingStepEnum> {
    const inProgressSteps = new Map<string, DocumentProcessingStepEnum>();
    const currentStates = this.tendersResponsesService.uploadStates();
    for (const scope of [SCOPE_DCE, SCOPE_INTERNAL_DOC]) {
      const key = this.tendersResponsesService.fileKey(this.currentProjectId!, scope);
      for (const f of currentStates[key]?.files ?? []) {
        if (f.processingStep === DocumentProcessingStepEnum.UPLOADING || f.processingStep === DocumentProcessingStepEnum.CATEGORIZING) {
          inProgressSteps.set(f.uid, f.processingStep);
        }
      }
    }
    return inProgressSteps;
  }

  onUploadStateChange(): void {
    this.responseModuleStateService.refresh();
  }

  startEditNewTitle(): void {
    this.editedTitle = '';
    this.isEditingTitle = true;
  }

  onNewTitleBlur(): void {
    if (!this.editedTitle.trim()) this.editedTitle = DEFAULT_PROJECT_NAME;
    this.isEditingTitle = false;
    this.tendersResponsesService.updateProjectName('new', this.editedTitle);
  }

  onNewTitleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!this.editedTitle.trim()) this.editedTitle = DEFAULT_PROJECT_NAME;
      this.isEditingTitle = false;
      this.tendersResponsesService.updateProjectName('new', this.editedTitle);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.editedTitle = DEFAULT_PROJECT_NAME;
      this.isEditingTitle = false;
      this.tendersResponsesService.updateProjectName('new', DEFAULT_PROJECT_NAME);
    }
  }

  async onNewProjectContinueClicked(): Promise<void> {
    const state = await firstValueFrom(this.responseModuleStateService.refresh());
    const nbAllowed = state?.nbAllowed ?? null;
    const nbCreated = state?.nbCreated ?? null;
    if (nbAllowed !== null && nbCreated !== null && nbCreated >= nbAllowed) {
      this.openBlockedModal('slot-conflict');
      return;
    }
    const name = this.editedTitle.trim() || DEFAULT_PROJECT_NAME;
    this.apiResponseProjects.createResponseProject({ name }).subscribe({
      next: created => {
        this.responseModuleStateService.refresh();
        this.tendersResponsesService.removeProject('new');
        this.tendersResponsesService.addProject(created);
        this.tendersResponsesService.newProjectId.set(created.id);
        void this.router.navigate(['/tenders/responses', created.id], { replaceUrl: true }).then(() => {
          this.blocksRevealed = true;
        });
      },
      error: () => {},
    });
  }

  onDceContinueClicked(): void {
    this.blocksRevealed = true;
    this.tendersResponsesService.newProjectId.set(null);
  }

  startEditTitle(projectName: string): void {
    this.editedTitle = projectName;
    this.isEditingTitle = true;
  }

  saveTitle(projectId: string, currentName: string): void {
    const name = this.editedTitle.trim();
    this.isEditingTitle = false;
    if (!name || name === currentName) return;
    this.tendersResponsesService.updateProjectName(projectId, name);
    this.project.update(p => (p ? { ...p, name } : null));
    this.apiResponseProjects.updateResponseProject({ id: projectId, name }).subscribe({
      error: (err: { status?: number }) => {
        if (err.status === 404) {
          this.tendersResponsesService.removeProject(projectId);
          this.blockedModalService.open('project-deleted');
        } else {
          this.tendersResponsesService.updateProjectName(projectId, currentName);
          this.project.update(p => (p ? { ...p, name: currentName } : null));
        }
      },
    });
  }

  cancelEditTitle(projectName: string): void {
    this.editedTitle = projectName;
    this.isEditingTitle = false;
  }

  onTitleKeyDown(event: KeyboardEvent, projectId: string, currentName: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveTitle(projectId, currentName);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditTitle(currentName);
    }
  }

  onDocumentNotFound(): void {
    this.blockedModalService.open('document-deleted', this.project()?.id ?? null);
  }

  onProjectNotFound(projectId: string): void {
    this.tendersResponsesService.removeProject(projectId);
    this.blockedModalService.open('project-deleted');
  }

  private openBlockedModal(mode: ResponseDialogMode): void {
    this.blockedModalService.open(mode, null, { isTrial: this.isTrial, planLabel: this.planLabel });
  }

  get mailtoHref(): string {
    return getCsmMailtoHref(this.isTrial ? ContactReason.TrialEnded : ContactReason.Expired);
  }

  async openOrCloseProject(projectId: string, shouldClose: boolean): Promise<void> {
    try {
      await firstValueFrom(this.apiResponseProjects.updateResponseProject({ id: projectId, is_active: !shouldClose }));
      this.project.update(p => (p ? { ...p, is_active: !shouldClose } : p));
      shouldClose ? this.tendersResponsesService.markProjectClosed(projectId) : this.tendersResponsesService.markProjectActive(projectId);
      this.responseModuleStateService.refresh();
      const toastKey = shouldClose ? 'tenders.responses.status-toast.closed' : 'tenders.responses.status-toast.reopened';
      this.toastService.show(this.translateService.instant(toastKey));
    } catch {}
  }

  openDeleteProjectModal(projectId: string, projectName: string): void {
    const modalRef = this.modalService.open(ResponseDialogComponent, {
      centered: true,
      windowClass: 'project-blocked-modal-dialog',
      backdropClass: 'blur-backdrop',
    });
    modalRef.componentInstance.mode = 'project-delete-confirm';
    modalRef.componentInstance.projectName = projectName;
    modalRef.result.then(
      () => this.deleteProject(projectId),
      () => {}
    );
  }

  private deleteProject(projectId: string): void {
    const projects = this.tendersResponsesService.projects();
    const index = projects.findIndex(p => p.id === projectId);
    let replacementProjectId: string | null = null;
    if (index >= 0 && index < projects.length - 1) {
      replacementProjectId = projects[index + 1].id;
    } else if (index > 0) {
      replacementProjectId = projects[index - 1].id;
    }

    this.apiResponseProjects.deleteResponseProject(projectId).subscribe({
      next: () => {
        this.responseModuleStateService.refresh();
        this.tendersResponsesService.removeProject(projectId);
        if (replacementProjectId) {
          void this.router.navigate(['/tenders/responses', replacementProjectId]);
        } else {
          void this.router.navigate(['/tenders/responses']);
        }
      },
      error: () => {},
    });
  }
}
